import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { debounce } from 'underscore';

import { StreamType } from 'rev-shared/webrtc/Contract';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { WebRtcListenerConnectionService } from 'rev-shared/webrtc/WebRtcListenerConnection.Service';
import { getLayoutVideo } from 'rev-shared/webrtc/ListenerPresenters';
import { isMobile, isSafari } from 'rev-shared/util/UserAgentUtil';

import { IProducerLayout, IProducerLayoutVideo, IProducerStreamsCfg } from '../../Contract';
import { ProducerService } from '../../Producer.Service';
import { ProducerControlsService } from './ProducerControls.Service';

import baseStyles from '../../ProducerWebcast.module.less';
import styles from './ProducerVideoControls.module.less';

export const dragOptions = {
	classes : { 'ui-draggable': 'vb-draggable-grab' },
	cursorAt: { top: 45, left: 110 }
};

@Component({
	host: {
		'[class]': 'styles.root'
	},
	selector: 'producer-video-controls',
	templateUrl: './ProducerVideoControls.Component.html',
	standalone: false
})
export class ProducerVideoControlsComponent implements OnInit, OnChanges, OnDestroy {
	@Input() public canEditLayout: boolean;
	@Input() public isExternal: boolean;
	@Input() public layoutData: IProducerLayout;
	@Input() public controlsOnly: boolean;
	@Input() public controlsRow: boolean;
	@Input() public showLabel: boolean;

	@ViewChild('cameraWrap') public cameraWrapElem: ElementRef;

	public streams: IProducerStreamsCfg = {} as any;
	public readonly StreamType = StreamType;

	public readonly baseStyles = baseStyles;
	public readonly dragOptions = dragOptions;
	public readonly styles = styles;
	public readonly isMobile = isMobile();
	public readonly isSafari = isSafari();
	private readonly userId = this.UserContext.getUser().id;
	public readonly userName = this.UserContext.getUser().fullName;
	public readonly getCaptureDragData = () => this.getDragData(StreamType.Content);
	public readonly getCameraDragData = () => this.getDragData(StreamType.Speaker);
	private readonly sub = new Subscription();

	public error: boolean;
	public captureProcessing: boolean;
	public cameraProcessing: boolean;
	public cameraEnabled: boolean;
	public micProcessing: boolean;
	public hasMicStream: boolean;

	public isSpeaking: boolean;

	public layoutTypes: Record<StreamType, boolean> = {} as any;

	constructor(
		private readonly ProducerControlsService: ProducerControlsService,
		private readonly ProducerService: ProducerService,
		private readonly UserContext: UserContextService,
		private readonly ListenerConnnection: WebRtcListenerConnectionService
	) {
	}

	public ngOnInit(): void {
		this.sub.add(this.ProducerService.producerStreams$.subscribe(streams => {
			this.streams = streams;
			this.cameraEnabled = !!streams.cameraStream;
			this.ProducerControlsService.tryUpdateCaptureDevices();
		}));

		const clearIsSpeaking = debounce(() => this.isSpeaking = false, 1000);

		this.sub.add(this.ListenerConnnection.presenters?.presenters$.subscribe(() => {
			this.updateLayoutTypes();
			const presenter = this.ListenerConnnection.presenters.getPresenter(this.userId);
			this.hasMicStream = presenter?.streams.some(s => s.producer.kind === StreamType.Audio);

			if(this.ListenerConnnection.presenters.isSpeaking(this.userId)) {
				this.isSpeaking = true;
				clearIsSpeaking();
			}
		}));
	}

	public ngOnChanges(): void {
		this.updateLayoutTypes();
	}

	private updateLayoutTypes(): void {
		this.layoutTypes = this.ListenerConnnection.presenters?.checkPresenterLayout(this.userId, this.layoutData);
	}

	public ngOnDestroy(): void {
		this.sub.unsubscribe();
	}

	public getDragData(type: StreamType): IProducerLayoutVideo {
		const presenter = this.ListenerConnnection.presenters?.getPresenter(this.userId);
		if(!presenter){
			console.error('ProducerVideoControls.getDragData presenter null');
			return;
		}

		return getLayoutVideo(presenter, type);
	}

	public setCamera(enabled: boolean, device: MediaDeviceInfo): void {
		if(!enabled) {
			this.ProducerControlsService.stopCamera();
			return;
		}

		Promise.all([
			this.ProducerControlsService.startCamera(device),
			!this.streams.micStream && this.ProducerControlsService.initMic(),
		])
			.catch(e => console.error('Error starting camera stream: ', e));

	}

	public setMic(enabled: boolean, device: MediaDeviceInfo): void {
		this.ProducerControlsService.toggleMic(enabled, device);
	}

	public toggleMic(): void {
		this.ProducerControlsService.toggleMic(
			this.streams.micMuted || !this.streams.micStream,
			this.streams.micDevice
		);
	}

	public toggleCapture(): void {
		this.streams.displayCaptureStream
			? this.stopCapture()
			: this.startCapture();
	}

	public startCapture(): void {
		this.ProducerControlsService.startCapture();
	}

	public stopCapture(): void {
		this.ProducerControlsService.stopCapture();
	}

	public stopMic(): void {
		this.ProducerControlsService.stopMic();
	}

	public addPresenter(type: StreamType, isBackground?: boolean): void {
		this.toggleLayoutStream(type, true, isBackground);
	}

	public removePresenter(type: StreamType): void {
		this.toggleLayoutStream(type, false);
	}

	public toggleLayoutAudio(): void {
		this.toggleLayoutStream(StreamType.Audio, !this.layoutTypes.audio);
	}

	public toggleLayoutStream(type: StreamType, active: boolean, isBackground?: boolean): void {
		const presenter = this.ListenerConnnection.presenters.getPresenter(this.userId);
		this.ProducerService.togglePresenterPreview(presenter, type, isBackground, active)
			.catch(e => console.log('PresenterVideoComponent, toggleLayoutStream: ', e));
	}
}
